// Convert the in-memory map state into a Google Maps link. Pure and DOM-free.
//
// Google Maps URLs (https://developers.google.com/maps/documentation/urls) take
// only points: one marker becomes a search link, several become a route. The
// route form allows nine waypoints on desktop, so eleven points in total, and
// the whole URL must stay under 2048 characters. Lines, polygons, text labels,
// colours and pin categories have no place in the format, so they are reported
// back as dropped features and the caller shows the loss.

import type { Line, MapState, Marker, Polygon, TextLabel } from "./types.ts";

// Which of the two Google Maps URL forms a state produces. "none" means the map
// has no markers, so there is nothing Google Maps can show.
export type GoogleUrlKind = "none" | "search" | "directions";

// The travel modes the directions form accepts.
export type GoogleTravelMode = "driving" | "walking" | "bicycling" | "transit";

// One piece of the map the link cannot carry, named so the user can see what is
// missing rather than only a count.
export interface DroppedFeature {
  kind: "marker" | "line" | "polygon" | "text";
  name: string;
  reason: string;
}

// Caller overrides. `travelMode` is added to a route link only when set, so the
// default keeps Google's own choice. `maxPoints` exists for tests and for
// callers that know they target mobile.
export interface GoogleUrlOptions {
  travelMode?: GoogleTravelMode;
  maxPoints?: number;
}

// The generated link: its form, the URL itself ("" when there is nothing to
// show), how many markers reached the URL, and everything left behind.
export interface GoogleMapsLink {
  kind: GoogleUrlKind;
  url: string;
  pointCount: number;
  dropped: DroppedFeature[];
}

// Origin, up to nine waypoints, and destination.
const MAX_ROUTE_POINTS = 11;

// Google Maps stops reading a URL after this many characters.
export const MAX_URL_LENGTH = 2048;

const SEARCH_BASE = "https://www.google.com/maps/search/?api=1";
const DIRECTIONS_BASE = "https://www.google.com/maps/dir/?api=1";

// A pipe separates waypoints. It is not a legal URL character, so it is encoded;
// commas are legal in a query value and stay readable.
const WAYPOINT_SEPARATOR = "%7C";

const DROP_REASON = {
  line: "Google Maps cannot show lines from a link.",
  polygon: "Google Maps cannot show polygons from a link.",
  text: "Google Maps cannot show text labels from a link.",
  marker:
    `Google Maps accepts at most ${MAX_ROUTE_POINTS} route points in a link.`,
} as const;

// Six decimal places is about 0.11 m — finer than the map itself stores — and it
// keeps the URL short. Number() strips the trailing zeros toFixed leaves behind.
function coordinate(value: number): string {
  return String(Number(value.toFixed(6)));
}

function point(marker: Marker): string {
  return `${coordinate(marker.lat)},${coordinate(marker.lng)}`;
}

function dropped(
  kind: DroppedFeature["kind"],
  name: string,
): DroppedFeature {
  return { kind, name, reason: DROP_REASON[kind] } satisfies DroppedFeature;
}

// Named after its own label where it has one, else by kind and position, so two
// unlabelled polygons still read as different features.
function featureName(
  labelled: Line | Polygon,
  fallback: string,
): string {
  return labelled.label && labelled.label.length > 0 ? labelled.label : fallback;
}

function markerName(marker: Marker, position: number): string {
  return marker.label && marker.label.length > 0
    ? marker.label
    : `Marker ${position}`;
}

function textName(text: TextLabel, position: number): string {
  return text.text.length > 0 ? text.text : `Text ${position}`;
}

// Everything the URL cannot carry: the markers past the route cap first, then
// each line, polygon and text label in map order.
function droppedFeatures(
  state: MapState,
  usedMarkers: number,
): DroppedFeature[] {
  const losses: DroppedFeature[] = [];

  const markers = state.markers ?? [];
  for (let index = usedMarkers; index < markers.length; index += 1) {
    losses.push(dropped("marker", markerName(markers[index], index + 1)));
  }

  (state.lines ?? []).forEach((line, index) => {
    losses.push(dropped("line", featureName(line, `Line ${index + 1}`)));
  });

  (state.polygons ?? []).forEach((polygon, index) => {
    losses.push(
      dropped("polygon", featureName(polygon, `Polygon ${index + 1}`)),
    );
  });

  (state.texts ?? []).forEach((text, index) => {
    losses.push(dropped("text", textName(text, index + 1)));
  });

  return losses;
}

function searchUrl(marker: Marker): string {
  return `${SEARCH_BASE}&query=${point(marker)}`;
}

function directionsUrl(
  markers: Marker[],
  travelMode?: GoogleTravelMode,
): string {
  const origin = point(markers[0]);
  const destination = point(markers[markers.length - 1]);
  const waypoints = markers.slice(1, -1).map(point);

  let url = `${DIRECTIONS_BASE}&origin=${origin}&destination=${destination}`;
  if (waypoints.length > 0) {
    url += `&waypoints=${waypoints.join(WAYPOINT_SEPARATOR)}`;
  }
  if (travelMode) {
    url += `&travelmode=${travelMode}`;
  }
  return url;
}

// How many markers may reach the URL: the route cap, or the caller's own lower
// limit. A limit below one is treated as one.
function pointLimit(options?: GoogleUrlOptions): number {
  const requested = options?.maxPoints ?? MAX_ROUTE_POINTS;
  return Math.max(1, Math.min(requested, MAX_ROUTE_POINTS));
}

// Build the best Google Maps link the state allows, plus the list of everything
// that could not come along.
export function stateToGoogleMapsUrl(
  state: MapState,
  options?: GoogleUrlOptions,
): GoogleMapsLink {
  const markers = state.markers ?? [];
  const used = markers.slice(0, pointLimit(options));

  if (used.length === 0) {
    return {
      kind: "none",
      url: "",
      pointCount: 0,
      dropped: droppedFeatures(state, 0),
    } satisfies GoogleMapsLink;
  }

  const kind = used.length === 1 ? "search" : "directions";
  const url = used.length === 1
    ? searchUrl(used[0])
    : directionsUrl(used, options?.travelMode);

  return {
    kind,
    url,
    pointCount: used.length,
    dropped: droppedFeatures(state, used.length),
  } satisfies GoogleMapsLink;
}
